import React from 'react';
import {
  Check,
  Calculator,
  BarChart,
  Users,
  Palette,
  Shield,
  Database,
  TrendingUp,
  Smartphone,
  Headphones,
  Crown,
  RefreshCw,
  Eye,
  Bell,
  Code,
} from 'lucide-react';

const FeaturesPage = () => {
  const features = [
    {
      category: 'Core Platform',
      items: [
        {
          icon: Calculator,
          title: 'Smart Workload Management',
          description:
            'Configure working days, team capacity, and holidays for accurate completion estimates.',
          highlights: [
            'Customizable working schedules (any days/hours)',
            'Team capacity tracking with realistic rates',
            'Holiday calendar integration',
            'Visual workload distribution analytics',
            'Automated date calculations',
          ],
        },
        {
          icon: RefreshCw,
          title: 'Bi-Directional Google Sheets Sync',
          description:
            'Edit your data directly in Google Sheets and see changes instantly in client portals.',
          highlights: [
            'Real-time synchronization with Google Sheets',
            'Edit status, notes, and dates directly in spreadsheet',
            'Instant portal updates without delays',
            'Maintain your existing workflow',
            'No data migration required',
          ],
        },
        {
          icon: Users,
          title: 'Multi-Client Portal Management',
          description:
            'Create and manage unlimited branded portals for different clients or projects.',
          highlights: [
            'Unlimited client portals (Business plan)',
            'Individual branding per portal',
            'Separate Google Sheets per client',
            'Custom domains for each portal',
            'Centralized management dashboard',
          ],
        },
      ],
    },
    {
      category: 'Client Experience',
      items: [
        {
          icon: Palette,
          title: 'Advanced Branding & Customization',
          description:
            'Create pixel-perfect branded experiences that match your company identity.',
          highlights: [
            'Custom logos, colors, and fonts',
            'White-label options (remove TrackerSheets branding)',
            'Custom domain support (status.yourcompany.com)',
            'Personalized welcome messages',
            'Industry-specific templates',
          ],
        },
        {
          icon: Smartphone,
          title: 'Mobile-Optimized Portals',
          description:
            'Perfect client experience on any device with responsive design.',
          highlights: [
            'Fully responsive design',
            'Touch-friendly interface',
            'Fast loading on mobile networks',
            'App-like experience',
            'Cross-platform compatibility',
          ],
        },
        {
          icon: Bell,
          title: 'Smart Notifications',
          description:
            'Keep clients informed with intelligent, customizable notifications.',
          highlights: [
            'Email notifications for status changes',
            'SMS alerts for urgent updates (premium)',
            'Customizable notification templates',
            'Automated progress updates',
            'Client preference management',
          ],
        },
      ],
    },
    {
      category: 'Business Intelligence',
      items: [
        {
          icon: BarChart,
          title: 'Advanced Analytics & Reporting',
          description:
            'Deep insights into your business performance and client engagement.',
          highlights: [
            'Client portal usage analytics',
            'Completion rate tracking',
            'Performance bottleneck identification',
            'Team productivity metrics',
            'Custom report generation',
          ],
        },
        {
          icon: TrendingUp,
          title: 'Capacity Planning & Forecasting',
          description:
            'Optimize your business operations with predictive analytics.',
          highlights: [
            'Demand forecasting based on historical data',
            'Capacity optimization recommendations',
            'Seasonal trend analysis',
            'Resource allocation insights',
            'Revenue projection tools',
          ],
        },
        {
          icon: Eye,
          title: 'Real-Time Business Dashboard',
          description:
            'Monitor your entire operation from a single, comprehensive dashboard.',
          highlights: [
            'Live status overview across all clients',
            'Team workload visualization',
            'Performance KPI tracking',
            'Client satisfaction metrics',
            'Financial performance indicators',
          ],
        },
      ],
    },
    {
      category: 'Enterprise & Integration',
      items: [
        {
          icon: Crown,
          title: 'Enterprise SaaS Deployment',
          description:
            'Scalable cloud deployment with dedicated infrastructure and premium support.',
          highlights: [
            'Private cloud deployment on your infrastructure',
            'Volume discounts for large organizations',
            'Custom feature development and integrations',
            'Dedicated customer success manager',
            '99.9% uptime SLA with priority support',
          ],
        },
        {
          icon: Code,
          title: 'Enterprise Perpetual License',
          description:
            'One-time licensing for unlimited use with complete white-label freedom.',
          highlights: [
            'Perpetual usage rights with no monthly fees',
            'Complete white-label customization',
            'Deploy on your own servers and infrastructure',
            'Unlimited client portals and users',
            'Optional ongoing support and update contracts',
          ],
        },

        {
          icon: Shield,
          title: 'Enterprise Security & Compliance',
          description:
            'Bank-level security with compliance features for regulated industries.',
          highlights: [
            'SOC 2 Type II compliance',
            'GDPR and CCPA compliant',
            'End-to-end encryption',
            'Role-based access controls',
            'Audit logs and compliance reporting',
          ],
        },
      ],
    },
    {
      category: 'Support & Success',
      items: [
        {
          icon: Headphones,
          title: 'Premium Support & Training',
          description:
            'Get the help you need to maximize your TrackerSheets investment.',
          highlights: [
            '24/7 priority support (Business plan)',
            'Dedicated customer success manager',
            'Live onboarding and training sessions',
            'Custom implementation assistance',
            'Best practices consultation',
          ],
        },
        {
          icon: Database,
          title: 'Data Security & Infrastructure',
          description:
            'Enterprise-grade data protection with flexible deployment options.',
          highlights: [
            'SOC 2 Type II compliant infrastructure',
            'Choose cloud or on-premises deployment',
            'Automated daily backups with point-in-time recovery',
            'Data residency options for compliance requirements',
            'Advanced monitoring and security incident response',
          ],
        },
        {
          icon: Crown,
          title: 'Professional Services & Custom Development',
          description:
            'Expert implementation and custom development to meet your unique requirements.',
          highlights: [
            'Custom portal design and development services',
            'Enterprise integration and workflow automation',
            'Dedicated implementation and migration support',
            'Training programs and change management',
            'Ongoing consulting and optimization services',
          ],
        },
      ],
    },
  ];

  const plans = [
    {
      name: 'Starter',
      price: '$29/mo',
      features: ['1 portal', 'Basic branding', 'Email support'],
      highlight: false,
    },
    {
      name: 'Professional',
      price: '$79/mo',
      features: ['5 portals', 'Advanced analytics', 'Priority support'],
      highlight: true,
    },
    {
      name: 'Business',
      price: '$199/mo',
      features: ['Unlimited portals', 'White-label', 'Custom domains'],
      highlight: false,
    },
    {
      name: 'Enterprise',
      price: 'Contact Sales',
      features: ['Custom deployment', 'Volume pricing', 'Perpetual licensing'],
      highlight: false,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 dark:bg-gray-900 dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <header className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-white">
            Everything You Need to Transform
            <br />
            <span className="text-blue-600">Client Communication</span>
          </h1>
          <p className="mt-5 max-w-3xl mx-auto text-xl text-gray-500 dark:text-gray-400">
            From simple status tracking to advanced business intelligence,
            TrackerSheets provides the complete platform for modern client
            communication and workflow management.
          </p>
        </div>
      </header>

      {/* Features Sections */}
      {features.map((category, categoryIndex) => (
        <section key={categoryIndex} className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl dark:text-white">
                {category.category}
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {category.items.map((feature, featureIndex) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={featureIndex}
                    className="bg-white p-6 rounded-lg shadow-md dark:bg-gray-800 dark:shadow-lg"
                  >
                    <div className="flex items-start mb-4">
                      <div className="flex-shrink-0 bg-blue-100 rounded-full p-3 dark:bg-blue-900">
                        <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {feature.title}
                        </h3>
                        <p className="mt-2 text-gray-600 dark:text-gray-400">
                          {feature.description}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4">
                      <ul className="space-y-2">
                        {feature.highlights.map((highlight, highlightIndex) => (
                          <li key={highlightIndex} className="flex items-start">
                            <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {highlight}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Pricing Overview */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl dark:text-white">
              Plans for Every Business
            </h2>
            <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">
              Start with what you need today, scale as you grow tomorrow.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative p-6 bg-gray-50 rounded-lg dark:bg-gray-800 ${
                  plan.highlight ? 'ring-2 ring-blue-600' : ''
                }`}
              >
                {plan.highlight && (
                  <div className="absolute top-0 inset-x-0 px-4 py-1 bg-blue-600 text-white text-center text-sm font-semibold rounded-t-lg ">
                    Most Popular
                  </div>
                )}
                <div className={plan.highlight ? 'pt-4' : ''}>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white ">
                    {plan.name}
                  </h3>
                  <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                    {plan.price}
                  </p>
                  <ul className="mt-4 space-y-2">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <Check className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="/pricing"
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-900 hover:text-white"
            >
              View Detailed Pricing
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
